// src/components/study/LeadCrcInput.js
//
// "Lead CRC" picker for the Add / Edit Study forms and the old /edit page (UI
// revamp, UI_REVAMP_ENABLED). Studies show the CRC's FULL name ("JALAK
// UPADHYAY"); the picker lists the full names on the site's CRC names list
// (Admin Dashboard → CRC names, services/crcNames.js) that are linked to an active
// coordinator, and saves the full name exactly as written there. Each name
// shows who it is on Manage Coordinators ("Jalak"). Still a plain string in
// LEAD_CRC, so nothing that reads it changes.
//
// A saved name that isn't on the list (older text, or someone who left) is kept
// and labelled, never dropped; X clears it so another can be picked.
//
// "Type a name instead" (user, 2026-11-03): a name can always be typed, so a site
// whose CRC names list is empty or incomplete can still save a Lead CRC. A typed
// name is the same plain string, shown as "not linked" until the list covers it.

import { useEffect, useMemo, useState } from "react";
import { X, UserCircle2 } from "lucide-react";
import { fetchCoordinators } from "../../services/coordinatorService";
import { loadCrcNames } from "../../services/crcNamesService";
import { coordinatorForLead, dropdownNames, normName } from "../../services/crcNames";
import SearchableSelect from "../SearchableSelect";

const SWITCH_CLS = "mt-1 text-xs font-medium text-purple-300 hover:text-white transition";

const LeadCrcInput = ({ value, onChange, label = "Lead CRC" }) => {
  const [data, setData] = useState({ roster: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([fetchCoordinators(), loadCrcNames()])
      .then(([roster, links]) => alive && setData({ roster, links }))
      .catch((err) => {
        console.error("Failed to load the CRC names:", err);
        if (alive) setLoadError(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const options = useMemo(() => dropdownNames(data.links, data.roster), [data]);
  const whoFor = useMemo(() => {
    const m = new Map();
    data.links.forEach((l) => m.set(normName(l.fullName), l.coordinator));
    return m;
  }, [data.links]);
  const current = String(value || "").trim();
  const currentWho = current ? coordinatorForLead(current, data.links, data.roster) : "";

  return (
    <div className="block">
      <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">{label}</span>
      {typing ? (
        <>
          <input
            type="text"
            autoFocus
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => onChange(e.target.value.trim())}
            placeholder="Type the CRC's name"
            aria-label={`${label} name`}
            className="w-full rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
          />
          <button
            type="button"
            onClick={() => {
              setTyping(false);
              onChange("");
            }}
            className={SWITCH_CLS}
          >
            Pick from the list instead
          </button>
        </>
      ) : current ? (
        <div className="flex items-center gap-2 rounded-xl bg-white/[0.06] border border-white/10 px-3 py-2">
          <UserCircle2 className="w-4 h-4 text-purple-300 shrink-0" />
          <span className="text-sm text-slate-100 truncate">{current}</span>
          {!loading && !loadError &&
            (currentWho && normName(currentWho) !== normName(current) ? (
              <span className="text-[10px] text-slate-400 shrink-0">({currentWho})</span>
            ) : !currentWho ? (
              <span className="text-[10px] text-amber-300 shrink-0">not linked to a coordinator</span>
            ) : null)}
          <button
            type="button"
            onClick={() => onChange("")}
            title="Clear to pick another CRC"
            aria-label="Clear Lead CRC"
            className="ml-auto text-slate-500 hover:text-rose-300 shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <>
          <SearchableSelect
            value=""
            options={options}
            loading={loading}
            onChange={(name) => onChange(name)}
            optionHint={(name) => {
              const who = whoFor.get(normName(name));
              return who && normName(who) !== normName(name) ? ` (${who})` : "";
            }}
            placeholder="Type to find a CRC"
            noMatchText="No CRC name matches that."
            emptyText="No CRC names yet. An admin can add them on the Admin Dashboard (CRC names)."
          />
          <button type="button" onClick={() => setTyping(true)} className={SWITCH_CLS}>
            Type a name instead
          </button>
        </>
      )}
      {loadError && (
        <p className="mt-1 text-[11px] text-rose-300">
          Couldn't load the CRC names. The saved Lead CRC is kept; try reopening this form.
        </p>
      )}
    </div>
  );
};

export default LeadCrcInput;
